import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  mergeMap,
  map,
  catchError,
  withLatestFrom,
  tap,
  debounceTime,
} from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
  dashboardData,
  dashboardCardData,
  dashboardDataSuccess,
  dashboardCardDataSuccess,
  dashboardDataFailure,
  dashboardCardDataFailure,
} from './dashboard.action';
import { DetailsService } from '../../admin/data-access/services/details.service';
import { UserStore } from '../../shared/data-access/stores/user.store';
import { AllocationUsageService } from '../../shared/data-access/services/allocation-usage.service';
import { DataService } from '../../shared/data-access/services/data.service';
import { Store } from '@ngrx/store';
import { Roles } from '../../authentication/data-access/models/auth.model';
import { currentFiscalYear } from '../../shared/utils/functions/dateUtils';
import { selectViewType, selectPaginationState } from './dashboard.selector';
import { AppState } from '../app.state';

@Injectable()
export class DashboardEffects {
  isOverviewer: boolean = false;

  constructor(
    private actions$: Actions,
    private shareDataService: DataService,
    private dataService: DetailsService,
    private userStore: UserStore,
    private allocationService: AllocationUsageService,
    private store: Store<AppState>,
  ) {}

  loadDashboardData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dashboardData),
      debounceTime(10),
      withLatestFrom(
        this.store.select(selectViewType),
        this.store.select(selectPaginationState),
        this.userStore.get(),
        this.shareDataService.date$,
        this.shareDataService.getUniversity$,
        this.shareDataService.departmentSubject$
      ),
      mergeMap(
        ([action, currentViewType, paginationState,user, currentDate, universityData,universityDepartment]) => {
          const viewType = action.viewType || currentViewType;
          this.isOverviewer = this.userStore.hasRole([Roles.OVERVIEWER]);
          const university = universityData?.university || '';
          const year = action.date || currentDate || new Date().getFullYear();
          const { pageIndex, pageSize } = paginationState;
          if (this.isOverviewer) {
            return this.dataService
              .getUniversityCardData(year, false, university, '', viewType, pageIndex, pageSize)
              .pipe(
                map((data) => dashboardDataSuccess({ payload: data.results })),
                catchError((error) => of(dashboardDataFailure({ error }))),
              );
          }
          return this.dataService
            .getDashboardData(year, university, user?.department || universityDepartment)
            .pipe(
              map((data) => dashboardDataSuccess({ payload: data.results })),
              catchError((error) => of(dashboardDataFailure({ error }))),
            );
        },
      ),
    ),
  );

  loadDashboardCardData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dashboardCardData),
      withLatestFrom(
        this.store.select(selectViewType),
        this.userStore.get(),
        this.shareDataService.getUniversity$,
        this.shareDataService.date$,
        this.shareDataService.departmentSubject$
      ),
      mergeMap(([_, currentViewType, user, universityData, currentYear,universityDepartment]) => {
        this.isOverviewer = this.userStore.hasRole([Roles.OVERVIEWER])
        const university = universityData?.university || user.university;
        const year = currentYear || new Date().getFullYear();
        const department = user?.department || universityDepartment;
        const faculty = user?.faculty || '';

        if (
          this.isOverviewer &&
          currentViewType === 'all'
        ) {
          return this.allocationService
            .getAllAllocationsForUniversity(university, year)
            .pipe(
              map((data) =>
                dashboardCardDataSuccess({ payload: data.results }),
              ),
              catchError((error) => of(dashboardCardDataFailure({ error }))),
            );
        }

        return this.allocationService
          .getAllocationsForDepartment(
            university,
            department,
            faculty,
            year.toString(),
          )
          .pipe(
            map((data) => dashboardCardDataSuccess({ payload: data.results })),
            catchError((error) => of(dashboardCardDataFailure({ error }))),
          );
      }),
    ),
  );

  handleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dashboardDataFailure, dashboardCardDataFailure),
        tap(({ error }) => {
        }),
      ),
    { dispatch: false },
  );
}
